import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Trash2, Pencil, Check, X, Wand2 } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { importMappingsApi, ImportMapping } from '@/lib/importMappings';

export function ImportMappingsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const mappings = useQuery({ queryKey: ['import-mappings', wsId], queryFn: () => importMappingsApi.list(wsId!), enabled: !!wsId });

  return (
    <AppShell title={t('mappings.title', { defaultValue: 'Import mappings' })} subtitle={t('mappings.subtitle', { defaultValue: 'Saved column mappings for recurring uploads.' })}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('mappings.title', { defaultValue: 'Import mappings' })} <span className="text-muted-foreground text-lg">取込マッピング</span></h1>
          <p className="mt-1 text-ink-600">{t('mappings.subtitle', { defaultValue: 'Reuse a saved column mapping on the next upload from the same source — no re-mapping. Save one from the Upload screen after reviewing a schema.' })}</p>
        </div>

        {mappings.isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          : (mappings.data ?? []).length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
              <Copy className="h-6 w-6 text-ink-300 mx-auto mb-2" />
              <p className="text-ink-700 font-medium">{t('mappings.empty', { defaultValue: 'No saved mappings yet.' })}</p>
              <p className="text-[13px] text-muted-foreground mt-1">{t('mappings.empty_hint', { defaultValue: 'Upload a file, review the proposed schema, then “Save as mapping”.' })}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(mappings.data ?? []).map((m) => <MappingRow key={m.id} wsId={wsId!} m={m} t={t} />)}
            </div>
          )}
      </main>
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function MappingRow({ wsId, m, t }: { wsId: string; m: ImportMapping; t: TFn }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(m.name);
  const [confirm, setConfirm] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['import-mappings', wsId] });
  const rename = useMutation({ mutationFn: () => importMappingsApi.update(wsId, m.id, { name: name.trim() }), onSuccess: () => { setEditing(false); invalidate(); } });
  const del = useMutation({ mutationFn: () => importMappingsApi.remove(wsId, m.id), onSuccess: invalidate });

  const periods = m.schema?.period_columns?.length ?? 0;
  const dims = Object.keys(m.schema?.dimensions ?? {}).length;

  return (
    <div className="rounded-md border border-border bg-white px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg border border-border px-2 py-1 text-sm" autoFocus />
            <button onClick={() => rename.mutate()} disabled={!name.trim() || rename.isPending} className="text-emerald-600 hover:text-emerald-700 p-1"><Check className="h-4 w-4" /></button>
            <button onClick={() => { setEditing(false); setName(m.name); }} className="text-ink-400 hover:text-ink-700 p-1"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <p className="font-semibold text-ink-900 truncate">{m.name}</p>
        )}
        <p className="text-[12px] text-muted-foreground mt-0.5 num">
          {m.schema?.account_code_column} · {t('mappings.periods', { defaultValue: `${periods} period col(s)`, count: periods })} · {t('mappings.dims', { defaultValue: `${dims} dim(s)`, count: dims })}
          {m.auto_run_allocations && <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-700"><Wand2 className="h-3 w-3" /> {t('mappings.auto', { defaultValue: 'auto-allocate' })}</span>}
          {m.use_count > 0 && <span className="ml-1.5">· {t('mappings.used', { defaultValue: `used ${m.use_count}×`, count: m.use_count })}</span>}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!editing && <button onClick={() => setEditing(true)} className="text-ink-400 hover:text-ink-700 p-1.5" title={t('common.rename', { defaultValue: 'Rename' })}><Pencil className="h-3.5 w-3.5" /></button>}
        {!confirm ? (
          <button onClick={() => setConfirm(true)} className="text-ink-400 hover:text-warn-600 p-1.5"><Trash2 className="h-3.5 w-3.5" /></button>
        ) : (
          <span className="inline-flex items-center gap-2 text-xs">
            <button onClick={() => del.mutate()} disabled={del.isPending} className="text-warn-700 font-semibold hover:underline">{del.isPending ? t('common.loading') : t('common.yes')}</button>
            <button onClick={() => setConfirm(false)} className="text-ink-500 hover:underline">{t('common.no')}</button>
          </span>
        )}
      </div>
    </div>
  );
}
